import { createFileRoute, Link, notFound, type ErrorComponentProps } from "@tanstack/react-router";
import { Check, Medal } from "lucide-react";
import { Reveal } from "@/components/site-chrome";
import { RequestService } from "@/components/request-service";
import { PageHero, Eyebrow, HowItWorks, FaqList, ReviewCard, ServiceCard, FinalCta, SitePhoto, faqLd } from "@/components/sections";
import { getService, SERVICES } from "@/lib/services-data";
import { reviews, SITE } from "@/lib/site-data";
import { pageHead, breadcrumbLd } from "@/lib/seo";

export const Route = createFileRoute("/services/$slug")({
  loader: ({ params }) => {
    const s = getService(params.slug);
    if (!s) throw notFound();
    return { slug: s.slug };
  },
  head: ({ loaderData }) => {
    const s = loaderData ? getService(loaderData.slug) : undefined;
    if (!s) return { meta: [{ title: "Service not found | 406 Mobile Mechanic" }, { name: "robots", content: "noindex" }] };
    const path = `/services/${s.slug}`;
    return pageHead(path, s.title, s.description, [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: s.name,
        serviceType: s.name,
        description: s.description,
        url: `${SITE}${path}`,
        areaServed: { "@type": "City", name: "Great Falls, MT" },
        provider: { "@type": "AutoRepair", name: "406 Mobile Mechanic", telephone: "+1-406-799-0095", url: SITE },
      },
      breadcrumbLd([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: s.name, path }]),
      faqLd(s.faqs),
    ]);
  },
  component: ServicePage,
  notFoundComponent: ServiceNotFound,
  errorComponent: ServiceError,
});

function ServiceNotFound() {
  return (
    <main className="topo bg-background px-4 py-24 text-center">
      <h1 className="text-5xl font-black">Service not found</h1>
      <p className="mt-4 text-muted-foreground">That service page doesn't exist.</p>
      <Link to="/services" className="label-mono mt-6 inline-block text-primary hover:underline">See all services →</Link>
    </main>
  );
}

function ServiceError({ error }: ErrorComponentProps) {
  return (
    <main className="bg-background px-4 py-24 text-center">
      <h1 className="text-4xl font-black">This page didn't load</h1>
      <p className="mt-4 text-muted-foreground">{error instanceof Error ? error.message : "Something went wrong."}</p>
      <Link to="/services" className="label-mono mt-6 inline-block text-primary hover:underline">Back to services →</Link>
    </main>
  );
}

function ServicePage() {
  const { slug } = Route.useLoaderData();
  const s = getService(slug)!;
  const related = s.related.map((r) => SERVICES.find((x) => x.slug === r)!).filter(Boolean);
  const words = s.h1.replace(/ in Great Falls, MT$/, "");
  return (
    <main>
      <PageHero
        crumbs={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: s.name }]}
        title={<>{words} in <span className="text-primary">Great Falls, MT</span></>}
        intro={s.intro}
        photo={s.photo}
      />

      {/* What's included + signs */}
      <section className="bg-background py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>The work</Eyebrow>
            <h2 className="mt-4 text-4xl font-black md:text-5xl">What's Included</h2>
            <ul className="mt-8 space-y-3">
              {s.included.map((i) => (
                <li key={i} className="flex items-start gap-3 border-b border-border pb-3"><Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />{i}</li>
              ))}
            </ul>
            {s.note && <p className="mt-6 border-l-2 border-primary pl-4 font-semibold">{s.note}</p>}
          </Reveal>
          <Reveal className="cut-corner border-2 border-dashed border-steel/50 bg-card p-8">
            <p className="label-mono text-steel">Symptoms</p>
            <h2 className="mt-2 text-4xl font-black md:text-5xl">Signs You Need {s.noun}</h2>
            <ul className="mt-6 space-y-3">
              {s.signs.map((i) => (
                <li key={i} className="flex items-start gap-3"><span className="mt-2 h-2 w-2 shrink-0 bg-primary" aria-hidden />{i}</li>
              ))}
            </ul>
            <div className="hazard mt-8 h-2 rounded-sm" aria-hidden />
          </Reveal>
        </div>
      </section>

      {/* Why on site */}
      <section className="bg-paper py-24 text-paper-foreground">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.2fr] lg:items-start">
          <Reveal>
            <Eyebrow>Mobile advantage</Eyebrow>
            <h2 className="mt-4 text-4xl font-black md:text-5xl">Why Get It Done On Site</h2>
            <p className="mt-5 text-lg">Josh shows you the problem firsthand, shows up when he says he will, and gives you an honest quote before the work starts.</p>
            <p className="mt-6 flex items-center gap-2 font-semibold"><Medal className="h-5 w-5 text-primary" aria-hidden />Military discount available — Malmstrom AFB families welcome.</p>
          </Reveal>
          <ul className="grid gap-px overflow-hidden rounded-sm bg-paper-foreground/15 sm:grid-cols-2">
            {s.onsite.map((o) => (
              <Reveal as="li" key={o.t} className="bg-paper p-6">
                <p className="font-display text-2xl font-extrabold uppercase">{o.t}</p>
                <p className="mt-2">{o.d}</p>
              </Reveal>
            ))}
            <Reveal as="li" className="bg-paper p-6">
              <p className="font-display text-2xl font-extrabold uppercase">Honest, reasonable quotes</p>
              <p className="mt-2">Parts sourced and a labor quote sent right away — no surprises.</p>
            </Reveal>
          </ul>
        </div>
      </section>

      <HowItWorks heading={`How ${s.name} Works`} />

      {/* FAQ + review */}
      <section className="bg-charcoal-deep py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <Eyebrow>Questions</Eyebrow>
            <h2 className="mt-4 text-4xl font-black md:text-5xl">{s.name} FAQs</h2>
            <FaqList items={s.faqs} />
          </div>
          <div>
            <Eyebrow>Google review</Eyebrow>
            <div className="mt-6 grid"><ReviewCard r={reviews[s.review]} /></div>
            <Link to="/reviews" className="label-mono mt-4 inline-block text-primary hover:underline">Read all reviews →</Link>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="topo bg-background py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Eyebrow>Related services</Eyebrow>
          <h2 className="mt-4 text-4xl font-black md:text-5xl">You May Also Need</h2>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {related.map((r) => <ServiceCard key={r.slug} s={r} />)}
          </div>
          <div className="mt-10 grid items-center gap-8 md:grid-cols-[1fr_2fr]">
            <SitePhoto name={s.photo === "truck" ? "lettering" : "truck"} sizes="(min-width: 768px) 33vw, 100vw" className="cut-corner aspect-[4/3] w-full object-cover" />
            <p className="text-lg text-foreground/85">
              {s.name} at your home, job site or roadside in Great Falls, Black Eagle, Malmstrom AFB, Vaughn, Belt, Cascade, Ulm, Sun River, Dutton, Fort Benton and across Montana.{" "}
              <Link to="/service-area" className="text-primary hover:underline">See the service area</Link>,{" "}
              <Link to="/services" className="text-primary hover:underline">browse all services</Link>, or{" "}
              <Link to="/contact" className="text-primary hover:underline">contact Josh</Link>.
            </p>
          </div>
        </div>
      </section>

      <FinalCta title={<>Need {s.noun.replace(/^an? /, "")}? <span className="text-primary">Call or text 406-799-0095</span></>} sub="Open 7 days, 8 AM–9 PM" />
      <RequestService defaultService={s.option} contactLink />
    </main>
  );
}
